import { NIGHT, WORLD_GEN } from "../constants";
import { clamp, vDist, type Vec2 } from "../math";
import { Rng } from "./rng";
import type { SunDef } from "./types";

/** Where the sphere is in a shutdown (see NIGHT): the stars faltering, dimming, out or relighting. */
export type OutagePhase = "falter" | "dimming" | "out" | "relight";

export interface Outage {
  phase: OutagePhase;
  /** Seconds since the shutdown began (the falter's start). */
  t: number;
  /** 0..1 through the phase. */
  u: number;
  /** How bright each star itself is, 0..1 (its disc and glow). */
  output: number;
  /** Seconds until the stars are fully relit. */
  left: number;
}

/** How long a shutdown lasts, all of it (s). */
export function outageLength(): number {
  return NIGHT.falter + NIGHT.dimming + NIGHT.out + NIGHT.relight;
}

/** When the sphere shuts down in slot `slot` (ms since the epoch), or null if it doesn't then. */
function shutdownStart(seed: number, slot: number): number | null {
  const rng = new Rng((seed ^ Math.imul(slot + 7, 0x85ebca6b) ^ 0x51a7e) >>> 0);
  if (rng.next() >= NIGHT.chance) return null;
  const slotMs = NIGHT.slot * 1000;
  // Never so early in its slot that its warning would have begun in the last: one at a time, in order.
  const lead = NIGHT.warning * 1000;
  return slot * slotMs + lead + Math.floor(rng.next() * (slotMs - lead - outageLength() * 1000));
}

/** A steady flicker, 0..1, from time alone (so every area and client agrees). */
function flicker(t: number): number {
  return 0.5 + 0.25 * Math.sin(t * 7.3) + 0.15 * Math.sin(t * 19.1 + 1.3) + 0.1 * Math.sin(t * 41.7 + 4.1);
}

/** Where a shutdown `t` seconds in is (see Outage), or null once it's over (or before it starts). */
export function outageState(t: number): Outage | null {
  if (t < 0) return null;
  const spans: [Outage["phase"], number][] = [["falter", NIGHT.falter], ["dimming", NIGHT.dimming], ["out", NIGHT.out], ["relight", NIGHT.relight]];
  const left = outageLength() - t;
  let at = t;
  for (const [phase, len] of spans) {
    if (at < len) {
      const u = at / len;
      const output = phase === "falter" ? 0.82 + 0.18 * flicker(t) : phase === "dimming" ? 1 - u : phase === "out" ? 0 : u * u;
      return { phase, t, u, output, left };
    }
    at -= len;
  }
  return null;
}

/**
 * Where the sphere is in a shutdown at wall-clock time `nowMs`, or null
 * when it isn't in one. Worked out from the world's seed and the time
 * alone, so every area, segment and client agrees without being told.
 */
export function shutdownAt(seed: number, nowMs: number): Outage | null {
  const slot = Math.floor(nowMs / (NIGHT.slot * 1000));
  for (const k of [slot, slot - 1]) {
    const start = shutdownStart(seed, k);
    if (start === null) continue;
    const o = outageState((nowMs - start) / 1000);
    if (o) return o;
  }
  return null;
}

/** When the next shutdown begins (ms since the epoch), looking a few slots ahead from `nowMs`; null if none is due in them. */
export function nextShutdown(seed: number, nowMs: number): number | null {
  const slot = Math.floor(nowMs / (NIGHT.slot * 1000));
  for (let k = slot; k < slot + 6; k++) {
    const start = shutdownStart(seed, k);
    if (start !== null && start > nowMs) return start;
  }
  return null;
}

/**
 * The warning (see NIGHT.warning): seconds until the sphere shuts down,
 * when that's within the warning; null otherwise (none due soon, or it's
 * already under way). The same everywhere, from the clock, so it costs no
 * messages.
 */
export function shutdownWarning(seed: number, nowMs: number): number | null {
  if (shutdownAt(seed, nowMs)) return null;
  const next = nextShutdown(seed, nowMs);
  if (next === null) return null;
  const left = (next - nowMs) / 1000;
  return left <= NIGHT.warning ? left : null;
}

/**
 * How much of a star's light reaches `gap` blocks past its surface during a
 * shutdown, 0..1: the dark walks in from its reach's edge to the star as it
 * dims, and the light sweeps back out as it relights, each with a soft edge.
 */
export function outageLight(o: Outage | null | undefined, gap: number): number {
  if (!o) return 1;
  const reach = WORLD_GEN.life.dark;
  const edge = NIGHT.edge;
  if (o.phase === "falter") return o.output;
  if (o.phase === "out") return 0;
  if (o.phase === "dimming") {
    const front = reach + edge - o.u * (reach + edge * 2);
    return clamp((front - gap) / edge, 0, 1) * (1 - o.u * 0.3);
  }
  const front = -edge + o.u * (reach + edge * 2);
  return clamp((front - gap) / edge, 0, 1) * (0.6 + 0.4 * o.u);
}

/** How a sun's light falls off with distance past its surface, 0..1 (as the life bands go, see WORLD_GEN.life). */
export function sunFalloff(gap: number): number {
  const { warm, dark } = WORLD_GEN.life;
  return gap <= warm[1] ? 1 : clamp(1 - (gap - warm[1]) / (dark - warm[1]), 0, 1);
}

/**
 * The stars' light at a point, 0..1, for the Hush (see NIGHT): the brightest
 * star's, a cold star giving `coldLight` of it, every one dimmed by the
 * sphere's shutdown (`outage`; missing, it's lit as usual).
 */
export function sunlightAt(suns: readonly SunDef[], p: Vec2, outage?: Outage | null): number {
  let best = 0;
  for (const s of suns) {
    const gap = Math.max(0, vDist(s.position, p) - s.radius);
    const l = sunFalloff(gap) * (s.kind === "cold" ? WORLD_GEN.life.coldLight : 1) * outageLight(outage, gap);
    if (l > best) best = l;
  }
  return best;
}

/** The sun whose system `p` is in (whose light reaches it, nearest first), if any. */
export function systemOf(suns: readonly SunDef[], p: Vec2): SunDef | undefined {
  let best: SunDef | undefined;
  let bestD = Infinity;
  for (const s of suns) {
    const d = vDist(s.position, p) - s.radius;
    if (d < WORLD_GEN.life.dark && d < bestD) {
      best = s;
      bestD = d;
    }
  }
  return best;
}
